import React from "react"
import type { JSONSchema7 } from "json-schema"
import { Text, Link, APIExample, Title } from "dash-styled"
import { sdkExamples } from "../../../../../../../Helpers/Functions/sdk"
import { getApiKey } from "../../../../../../../Helpers/Functions/Selectors/auth"
import { useAppState } from "../../../../../../../Hooks/useReduxState"
import { useAppInstance } from "../../../../../../../Hooks/useAppInstance"
import { hasValue } from "../../../../../../../Models/Client/FetchState"
import { JSONObj } from "../../../../../../../Helpers/Functions/convert"
import { findExamples } from "../../../../../../../Helpers/Schema/Schema"

interface Props {
    appID: string
    testId?: string
    schema: JSONSchema7
}

interface SDKIsolatedProps extends Props {
    apiKey: string
    appID: string
    apiUrl: string
}

/**
 * the sdk component is used in the context of this variant,
 * which means it will be used in 2 places:
 * - onboarding (add input data)
 * - dashboard add job
 *
 * Later, we might extend this to:
 * - dashboard add ground truth
 *
 * All require an API key, so the user will also be logged in.
 *
 * If they use the SDK version then polling isn't relevant, as
 * we don't know if there is one or many jobs being submitted.
 * So the process is likely to be identical.
 *
 * SDK then needs:
 * - api key
 * - route (to find context for endpoints)
 * - code for submission for each "type"
 *
 * It has 3 variants:
 * - curl (no installation req)
 * - python sdk (from github)
 * - cli sdk (from github)
 */

enum Context {
    jobs = "jobs"
}

export const SDKIsolated: React.FC<SDKIsolatedProps> = ({
    schema,
    apiKey,
    apiUrl,
    appID,
    testId
}: SDKIsolatedProps) => {
    const context: Context = Context.jobs // get context
    const example = sdkExamples[context]
    const data = findExamples(schema, true)[0] as JSONObj

    const exampleItems = {
        jobs: {
            cURL: {
                language: "bash" as "bash",
                snippet: example.curl.create(apiUrl, apiKey, appID, data)
            },
            Python: {
                language: "python" as "python",
                snippet: example.python.create(apiKey, appID, data)
            },
            CLI: {
                language: "bash" as "bash",
                snippet: example.cli.create(appID, data)
            }
        }
    }

    const apiUrls = {
        jobs: `${apiUrl}/apps/${appID}/jobs`
    }

    const renderAPI = Object.entries(exampleItems[context]).map(
        ([key, value]) => {
            return (
                <div key={key} className="mb-8">
                    <Title component="h3" variant="heading2">
                        Using {key === "CLI" ? "our " : ""}
                        {key}
                    </Title>
                    <APIExample
                        exampleItems={{ [key]: value }}
                        requestType={"POST"}
                        requestURL={apiUrls[context]}
                    />
                </div>
            )
        }
    )

    return (
        <div className="m-6 mb-0" data-testid={testId}>
            <Text>
                Our SDKs allow you to use your own tools to interface with our
                API. Our Python SDK and CLI, as well as installation
                instructions,{" "}
                <Link
                    to="https://github.com/mysuperai/superai-api-client"
                    target="_blank"
                >
                    are available on Github.
                </Link>
            </Text>
            {renderAPI}
        </div>
    )
}

export const SDK: React.FC<Props> = ({ appID, schema }: Props) => {
    const apiKey = useAppState(getApiKey)
    const appFetch = useAppInstance(appID)
    const apiUrl = useAppState(state => state.client.endpoints.apiUrl)

    if (!apiKey || !hasValue(appFetch) || !apiUrl) return null

    const props: SDKIsolatedProps = {
        schema,
        apiUrl,
        apiKey,
        appID: appFetch.value.uuid
    }

    return <SDKIsolated {...props} />
}
